import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClipboardCheck, ShieldAlert, ImageIcon, StickyNote, Loader2, RefreshCw, Trash2, Plus,
  FileText, AlertTriangle, ChevronDown, ChevronRight, ExternalLink, Link2, Upload, Sparkles, X, ZoomIn, BarChart3 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const uid = () => Math.random().toString(36).slice(2, 10);
const krf = (v) => `${Math.round(v || 0).toLocaleString("da-DK")} kr.`;

export const INPUT_ICON = { audit: ClipboardCheck, reklamation: ShieldAlert, reklanalyse: Sparkles, ledelsesrapport: BarChart3, kpi: ImageIcon, note: StickyNote };
export const INPUT_LABEL = { audit: "Intern audit", reklamation: "Reklamationer", reklanalyse: "Reklamationsanalyse", ledelsesrapport: "Ledelsesrapport", kpi: "KPI-billede", note: "Note / bilag" };
export function inputSummary(inp) {
  const t = inp.type;
  if (t === "audit") { const a = inp.audits || []; return { title: a.length === 1 ? `${a[0].audit_no} · ${a[0].title}` : `${a.length} audits`, meta: a.map((x) => daDate(x.date)).join(", ") }; }
  if (t === "reklamation") return { title: `Reklamationer · ${inp.stats?.label || ""}`, meta: inp.stats ? `${inp.stats.total} i alt · ${inp.stats.open} åbne · ${inp.stats.closed} lukkede` : "" };
  if (t === "reklanalyse") return { title: inp.title || "Reklamationsanalyse", meta: [inp.count != null ? `${inp.count} reklamationer` : null, inp.created_at ? daDate(inp.created_at) : null].filter(Boolean).join(" · ") };
  if (t === "ledelsesrapport") return { title: `Ledelsesrapport · ${inp.report?.label || inp.report?.period || ""}`, meta: inp.fetched_at ? `Hentet ${daDate(inp.fetched_at)}` : "" };
  const n = (inp.images || []).length, d = (inp.docs || []).length;
  const first = (inp.text || "").split("\n")[0].slice(0, 60) || (inp.images?.[0]?.caption) || inp.docs?.[0]?.filename || (t === "kpi" ? "KPI-billede" : "Note");
  return { title: first, meta: [n ? `${n} ${n === 1 ? "billede" : "billeder"}` : null, d ? `${d} bilag` : null, inp.fetched_at ? daDate(inp.fetched_at) : null].filter(Boolean).join(" · ") };
}

export async function uploadLeFile(meetingId, file) {
  const fd = new FormData(); fd.append("file", file);
  const r = await api.post(`/le/meetings/${meetingId}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data;
}
export async function createMediaInput(meetingId, file, type) {
  const up = await uploadLeFile(meetingId, file);
  const isImg = type === "kpi" || (file.type || "").startsWith("image/");
  return { id: uid(), type, text: "", images: isImg ? [{ image_id: up.id, caption: "" }] : [], docs: isImg ? [] : [{ image_id: up.id, filename: up.filename || file.name }], fetched_at: new Date().toISOString() };
}

export function DropField({ onFile, hint = "Træk en fil hertil, eller Ctrl+V et skærmbillede", testid = "le-dropfield", accept }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div tabIndex={0} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
      onPaste={(e) => { const it = [...(e.clipboardData?.items || [])].find((i) => i.kind === "file"); const f = it?.getAsFile(); if (f) { e.preventDefault(); e.stopPropagation(); onFile(f); } }}
      className={`rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors outline-none focus:border-stone-500 ${over ? "border-stone-600 bg-[#F3F1EC]" : "border-[#D6D3D1] bg-[#FBFAF8]"}`} data-testid={testid}>
      <Upload className="h-5 w-5 mx-auto text-stone-400" />
      <p className="text-sm text-stone-600 mt-2">{hint}</p>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
      <button type="button" onClick={() => ref.current?.click()} className="mt-2 text-sm font-medium text-[#1A1919] underline underline-offset-2" data-testid={`${testid}-choose`}>Vælg fil</button>
    </div>
  );
}

const TYPES = [
  { key: "audit", label: "Intern audit", icon: ClipboardCheck, desc: "Vælg afsluttede auditrapporter" },
  { key: "reklamation", label: "Reklamationer", icon: ShieldAlert, desc: "Statistik for en periode" },
  { key: "reklanalyse", label: "Reklamationsanalyse", icon: Sparkles, desc: "Hent en gemt årsagsanalyse" },
  { key: "ledelsesrapport", label: "Ledelsesrapport", icon: BarChart3, desc: "Nøgletal for en periode" },
  { key: "kpi", label: "KPI-billede", icon: ImageIcon, desc: "Upload eller indsæt screendumps" },
  { key: "note", label: "Egen note eller bilag", icon: StickyNote, desc: "Notat og vedhæftede filer" },
];

// ---------------- Audit-input ----------------
function AuditDialog({ meetingDate, onDone, onCancel }) {
  const [audits, setAudits] = useState([]);
  const [sel, setSel] = useState([]);
  const [assessment, setAssessment] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/le/sources/audits").then((r) => setAudits(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const done = () => {
    if (sel.length === 0) { toast.error("Vælg mindst én audit"); return; }
    const chosen = audits.filter((a) => sel.includes(a.id));
    onDone({ id: uid(), type: "audit", audit_ids: sel, assessment,
      audits: chosen.map((a) => ({ id: a.id, audit_no: a.audit_no, title: a.title, date: a.date, areas: a.areas, counts: a.counts, behandlet_le: a.behandlet_le })),
      fetched_at: new Date().toISOString() });
  };
  return (
    <div className="space-y-4" data-testid="le-input-audit">
      {loading ? <div className="flex items-center gap-2 text-stone-400 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser audits…</div> : audits.length === 0 ? (
        <p className="text-sm text-stone-500 py-6">Ingen afsluttede audits fundet.</p>
      ) : (
        <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
          {audits.map((a) => (
            <label key={a.id} data-testid={`le-audit-opt-${a.id}`} className={`block rounded-xl border p-3 cursor-pointer ${sel.includes(a.id) ? "border-[#A83232] bg-[#FBF6F5]" : "border-[#EBE8E3]"}`}>
              <div className="flex items-start gap-2.5">
                <input type="checkbox" checked={sel.includes(a.id)} onChange={() => toggle(a.id)} className="mt-1 accent-[#A83232]" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-mono text-xs text-stone-400">{a.audit_no}</span><span className="text-xs text-stone-500">{daDate(a.date)}</span>
                    {a.behandlet_le ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#E7EADF] text-[#5f6b4a]">Behandlet på LE</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F4EFEC] text-[#806B61]">Ikke behandlet på LE</span>}</div>
                  <p className="text-sm font-medium text-[#1A1919] mt-0.5">{a.title}</p>
                  <p className="text-xs text-stone-500">Områder: {(a.areas || []).join(", ") || "—"}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1 text-[11px]">
                    <span className="px-1.5 py-0.5 rounded bg-[#F1DEDA] text-[#A83232]">{a.counts?.Afvigelse || 0} afv.</span>
                    <span className="px-1.5 py-0.5 rounded bg-[#F4EFEC] text-[#806B61]">{a.counts?.Observation || 0} obs.</span>
                    <span className="px-1.5 py-0.5 rounded bg-[#E7EADF] text-[#5f6b4a]">{a.counts?.Forslag || 0} forslag</span>
                    {(a.counts?.open || 0) > 0 && <span className="px-1.5 py-0.5 rounded bg-white border border-[#E9C9C3] text-[#A83232]">{a.counts.open} ikke-lukkede</span>}
                  </div>
                </div>
              </div>
            </label>
          ))}
        </div>
      )}
      <div><p className="text-xs font-medium text-stone-500 mb-1.5">Ledelsens vurdering (valgfri)</p><Textarea data-testid="le-audit-assessment" value={assessment} onChange={(e) => setAssessment(e.target.value)} rows={2} className="rounded-xl" /></div>
      <DialogFooter><Button variant="ghost" onClick={onCancel} className="rounded-xl">Tilbage</Button><Button data-testid="le-audit-add" onClick={done} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">Tilføj</Button></DialogFooter>
    </div>
  );
}

// ---------------- Reklamation-input ----------------
function ReklamationDialog({ meetingDate, onDone, onCancel }) {
  const [period, setPeriod] = useState("moedemaaned");
  const [rtype, setRtype] = useState("reklamation");
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [stats, setStats] = useState(null);
  const [assessment, setAssessment] = useState("");
  const [selCases, setSelCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCases, setShowCases] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const params = { period, meeting_date: meetingDate, rtype };
      if (period === "custom") { params.date_from = from; params.date_to = to; }
      const r = await api.get("/le/sources/reklamationer", { params });
      setStats(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { if (period !== "custom") fetch(); }, [period, rtype]); // eslint-disable-line

  const done = () => {
    if (!stats) { toast.error("Hent statistik først"); return; }
    onDone({ id: uid(), type: "reklamation", assessment,
      stats: { ...stats, selected_case_ids: selCases }, fetched_at: stats.fetched_at });
  };
  const toggleCase = (id) => setSelCases((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <div className="space-y-4" data-testid="le-input-reklamation">
      <div className="grid grid-cols-2 gap-3">
        <div><p className="text-xs font-medium text-stone-500 mb-1.5">Type</p>
          <select data-testid="le-rek-rtype" value={rtype} onChange={(e) => setRtype(e.target.value)} className="h-10 w-full px-3 rounded-xl border border-[#EBE8E3] bg-white text-sm">
            <option value="reklamation">Kvalitet (reklamationer)</option><option value="indkob_reklamation">Indkøb (leverandørreklamationer)</option>
          </select></div>
        <div><p className="text-xs font-medium text-stone-500 mb-1.5">Periode</p>
          <select data-testid="le-rek-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-10 w-full px-3 rounded-xl border border-[#EBE8E3] bg-white text-sm">
            <option value="moedemaaned">Mødemåneden frem til mødedato</option><option value="forrige_maaned">Forrige måned</option><option value="custom">Valgfri fra/til</option>
          </select></div>
      </div>
      {period === "custom" && (
        <div className="grid grid-cols-2 gap-3 items-end">
          <div><p className="text-xs font-medium text-stone-500 mb-1.5">Fra</p><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-xl h-10" data-testid="le-rek-from" /></div>
          <div className="flex gap-2"><div className="flex-1"><p className="text-xs font-medium text-stone-500 mb-1.5">Til</p><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl h-10" data-testid="le-rek-to" /></div>
            <Button variant="outline" onClick={fetch} className="rounded-xl border-[#EBE8E3] h-10" data-testid="le-rek-fetch">Hent</Button></div>
        </div>
      )}
      {loading ? <div className="flex items-center gap-2 text-stone-400 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Henter…</div> : stats && (
        <div className="rounded-xl border border-[#EBE8E3] p-3.5 bg-[#FBFAF8]">
          <p className="text-xs text-stone-500">{stats.label} · <span className="italic">bygger på oprettelsesdato</span></p>
          <div className="flex flex-wrap gap-2 mt-2 text-sm">
            <span className="px-2.5 py-1 rounded-lg bg-white border border-[#EBE8E3]" data-testid="le-rek-total">I alt: <b>{stats.total}</b></span>
            <span className="px-2.5 py-1 rounded-lg bg-white border border-[#EBE8E3]">Åbne: <b className="text-[#A83232]">{stats.open}</b></span>
            <span className="px-2.5 py-1 rounded-lg bg-white border border-[#EBE8E3]">Lukkede: <b className="text-[#5f6b4a]">{stats.closed}</b></span>
          </div>
          {stats.by_customer?.length > 0 && <div className="mt-2 text-xs text-stone-600"><span className="text-stone-400">Pr. kunde: </span>{stats.by_customer.slice(0, 6).map((c) => `${c.name} (${c.count})`).join(", ")}</div>}
          <div className="mt-1 text-xs text-stone-600"><span className="text-stone-400">Pr. årsag: </span>{stats.has_category_data ? stats.by_category.map((c) => `${c.name} (${c.count})`).join(", ") : <span className="italic text-stone-400">Ingen fejlkategori registreret</span>}</div>
          {stats.cases.length > 0 && (
            <div className="mt-2">
              <button onClick={() => setShowCases((s) => !s)} className="text-xs text-[#806B61] flex items-center gap-1" data-testid="le-rek-toggle-cases">{showCases ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} Fold periodens {stats.cases.length} reklamationer ud</button>
              {showCases && <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                {stats.cases.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-white cursor-pointer">
                    <input type="checkbox" checked={selCases.includes(c.id)} onChange={() => toggleCase(c.id)} className="accent-[#A83232]" />
                    <span className="font-mono text-stone-400">{c.complaint_no || c.item_no || "—"}</span>
                    <span className="text-stone-600 truncate flex-1">{c.customer_name} · {c.title}</span>
                    <span className="text-stone-400">{daDate(c.created_at)}</span>
                    <a href={`/kvalitetsrapport/${c.id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#806B61] hover:text-[#A83232]"><ExternalLink className="h-3 w-3" /></a>
                  </label>
                ))}
              </div>}
              {selCases.length > 0 && <p className="text-[11px] text-stone-400 mt-1">{selCases.length} sag(er) markeret til drøftelse. Statistikken dækker alle {stats.total} reklamationer i perioden.</p>}
            </div>
          )}
        </div>
      )}
      <div><p className="text-xs font-medium text-stone-500 mb-1.5">Ledelsens vurdering (valgfri)</p><Textarea data-testid="le-rek-assessment" value={assessment} onChange={(e) => setAssessment(e.target.value)} rows={2} className="rounded-xl" /></div>
      <DialogFooter><Button variant="ghost" onClick={onCancel} className="rounded-xl">Tilbage</Button><Button data-testid="le-rek-add" onClick={done} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">Tilføj</Button></DialogFooter>
    </div>
  );
}

// ---------------- Reklamationsanalyse-input (gemt årsagsanalyse) ----------------
function ReklAnalyseDialog({ onDone, onCancel }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState("");
  useEffect(() => {
    Promise.all([api.get("/reklamation-analyser?rtype=reklamation"), api.get("/reklamation-analyser?rtype=indkob_reklamation")])
      .then(([a, b]) => setList([...(a.data || []), ...(b.data || [])])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const pick = (an) => onDone({ id: uid(), type: "reklanalyse", analyse_id: an.id, title: an.title || "Årsagsanalyse",
    rtype: an.report_type, count: an.count, created_at: an.created_at,
    summary: an.analysis?.summary || "", root_cause: an.analysis?.root_cause || "",
    five_whys: an.analysis?.five_whys || [],
    korrigerende: (an.override_korrigerende || "").trim() ? [an.override_korrigerende.trim()] : (an.analysis?.recommendations?.korrigerende || []),
    forebyggende: (an.override_forebyggende || "").trim() ? [an.override_forebyggende.trim()] : (an.analysis?.recommendations?.forebyggende || []),
    active_action: an.active_action || "", status: an.status || "", assessment });
  return (
    <div className="space-y-4" data-testid="le-input-reklanalyse">
      {loading ? <div className="flex items-center gap-2 text-stone-400 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser analyser…</div> : list.length === 0 ? (
        <p className="text-sm text-stone-500 py-6">Ingen gemte reklamationsanalyser fundet.</p>
      ) : (
        <>
          <div><p className="text-xs font-medium text-stone-500 mb-1.5">Ledelsens vurdering (valgfri)</p><Textarea value={assessment} onChange={(e) => setAssessment(e.target.value)} rows={2} className="rounded-xl" data-testid="le-reklanalyse-assessment" /></div>
          <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
            {list.map((an) => (
              <button key={an.id} data-testid={`le-reklanalyse-opt-${an.id}`} onClick={() => pick(an)} className="w-full text-left rounded-xl border border-[#EBE8E3] p-3 hover:border-[#A83232] hover:bg-[#FBF6F5]">
                <div className="flex items-center gap-2 flex-wrap"><Sparkles className="h-3.5 w-3.5 text-[#806B61]" /><span className="text-sm font-medium text-[#1A1919]">{an.title || "Årsagsanalyse"}</span>
                  {an.status && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F4EFEC] text-[#806B61]">{an.status}</span>}</div>
                <p className="text-xs text-stone-500 mt-0.5">{an.count} reklamationer · {daDate(an.created_at)}</p>
                {an.analysis?.root_cause && <p className="text-xs text-stone-600 mt-1 line-clamp-2">{an.analysis.root_cause}</p>}
              </button>
            ))}
          </div>
        </>
      )}
      <DialogFooter><Button variant="ghost" onClick={onCancel} className="rounded-xl">Tilbage</Button></DialogFooter>
    </div>
  );
}

// ---------------- KPI / Note-input (billeder + tekst + bilag) ----------------
function MediaDialog({ type, meetingId, onDone, onCancel }) {
  const [text, setText] = useState("");
  const [images, setImages] = useState([]); // {image_id, caption, url}
  const [docs, setDocs] = useState([]);     // {image_id, filename}
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null); const docRef = useRef(null);

  const upload = async (file, isImage) => {
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/le/meetings/${meetingId}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (isImage) setImages((im) => [...im, { image_id: r.data.id, caption: "", url: `${BACKEND}/api/le/images/${r.data.id}` }]);
      else setDocs((d) => [...d, { image_id: r.data.id, filename: r.data.filename }]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const onPaste = (e) => {
    const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
    if (item) { const f = item.getAsFile(); if (f) upload(f, true); }
  };
  const done = () => {
    if (!text.trim() && images.length === 0 && docs.length === 0) { toast.error("Tilføj billede, note eller bilag"); return; }
    onDone({ id: uid(), type, text, images: images.map(({ image_id, caption }) => ({ image_id, caption })),
      docs: docs.map(({ image_id, filename }) => ({ image_id, filename })), fetched_at: new Date().toISOString() });
  };
  return (
    <div className="space-y-4" data-testid={`le-input-${type}`} onPaste={onPaste}>
      <DropField onFile={(f) => upload(f, (f.type || "").startsWith("image/"))} testid="le-media-dropfield" />
      <div className="flex gap-2 items-center">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, true); e.target.value = ""; }} />
        <input ref={docRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, false); e.target.value = ""; }} />
        <button type="button" onClick={() => docRef.current?.click()} className="text-xs text-stone-600 hover:text-stone-900 flex items-center gap-1" data-testid="le-media-upload-doc"><FileText className="h-3.5 w-3.5" /> Vedhæft bilag (ikke-billede)</button>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-stone-400" />}
      </div>
      {images.length > 0 && <div className="grid grid-cols-2 gap-3">
        {images.map((im, i) => (
          <div key={im.image_id} className="rounded-xl border border-[#EBE8E3] p-2">
            <img src={im.url} alt="" className="w-full h-28 object-contain rounded-lg bg-[#FBFAF8]" />
            <Input value={im.caption} onChange={(e) => setImages((arr) => arr.map((x, j) => j === i ? { ...x, caption: e.target.value } : x))} placeholder="Billedtekst…" className="rounded-lg h-8 mt-1.5 text-xs" />
            <button onClick={() => setImages((arr) => arr.filter((_, j) => j !== i))} className="text-[11px] text-[#A83232] mt-1 hover:underline">Fjern</button>
          </div>
        ))}
      </div>}
      {docs.length > 0 && <div className="space-y-1">{docs.map((d, i) => (
        <div key={d.image_id} className="flex items-center gap-2 text-sm bg-[#FBFAF8] rounded-lg px-3 py-1.5"><FileText className="h-4 w-4 text-stone-400" /><span className="truncate flex-1">{d.filename}</span><button onClick={() => setDocs((arr) => arr.filter((_, j) => j !== i))} className="text-[#A83232]"><Trash2 className="h-3.5 w-3.5" /></button></div>
      ))}</div>}
      <div><p className="text-xs font-medium text-stone-500 mb-1.5">{type === "kpi" ? "Kommentar til KPI (valgfri)" : "Note"}</p><Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className="rounded-xl" data-testid="le-media-text" /></div>
      <DialogFooter><Button variant="ghost" onClick={onCancel} className="rounded-xl">Tilbage</Button><Button data-testid="le-media-add" onClick={done} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">Tilføj</Button></DialogFooter>
    </div>
  );
}

// ---------------- Ledelsesrapport-input (nøgletal for en periode) ----------------
function LedRapStat({ label, value }) {
  return (
    <div className="rounded-lg bg-white border border-[#EBE8E3] px-2.5 py-2">
      <p className="text-[10px] font-medium uppercase tracking-wide text-stone-400">{label}</p>
      <p className="text-sm font-semibold text-[#1A1919] mt-0.5">{value}</p>
    </div>
  );
}

export function LedelsesrapportView({ r }) {
  const k = r.kpi || {};
  const period = (r.date_from || r.date_to)
    ? `${r.date_from ? daDate(r.date_from) : "…"} – ${r.date_to ? daDate(r.date_to) : "…"}`
    : "Hele perioden";
  return (
    <div className="rounded-lg bg-[#FBFAF8] border border-[#F0EDE8] p-3" data-testid="le-ledrap-view">
      <p className="text-xs text-stone-500">Periode: <b className="text-stone-700">{period}</b></p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
        <LedRapStat label="Projekter" value={k.projects ?? 0} />
        <LedRapStat label="Kunder" value={k.customers ?? 0} />
        <LedRapStat label="Kunde økonomi" value={krf(k.total_budget_kr)} />
        <LedRapStat label="BSM økonomi" value={krf(k.total_realized_kr)} />
      </div>
      <div className="flex flex-wrap gap-2 mt-2 text-xs">
        <span className="px-2 py-0.5 rounded bg-white border border-[#EBE8E3]">Difference: <b className={(k.variance_kr || 0) < 0 ? "text-[#A83232]" : "text-[#5f6b4a]"}>{krf(k.variance_kr)}</b></span>
        <span className="px-2 py-0.5 rounded bg-white border border-[#EBE8E3]">Gns. varighed: <b>{r.durations?.avg_days ?? 0} dage</b> ({r.durations?.count ?? 0})</span>
      </div>
      {(r.top_customers || []).length > 0 && <div className="mt-2.5">
        <p className="text-[11px] font-semibold text-stone-500 mb-1">Største kunder (Kunde vs. BSM økonomi)</p>
        <div className="space-y-0.5">
          {r.top_customers.map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="text-stone-600 truncate flex-1">{c.customer} <span className="text-stone-400">· {c.projects} proj.</span></span>
              <span className="text-stone-500 shrink-0">{krf(c.budget_kr)} / <b className="text-stone-700">{krf(c.realized_kr)}</b></span>
            </div>
          ))}
        </div>
      </div>}
      {(r.adherence || []).length > 0 && <div className="mt-2.5">
        <p className="text-[11px] font-semibold text-stone-500 mb-1">Deadline-overholdelse pr. medarbejder</p>
        <div className="space-y-0.5">
          {r.adherence.map((e, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="text-stone-600 truncate flex-1">{e.name}</span>
              <span className={`shrink-0 font-semibold ${e.pct >= 80 ? "text-[#5f6b4a]" : e.pct >= 50 ? "text-[#806B61]" : "text-[#A83232]"}`}>{e.pct}%</span>
              <span className="text-stone-400 shrink-0">{e.late} forsinket / {e.total}</span>
            </div>
          ))}
        </div>
      </div>}
    </div>
  );
}

function LedelsesrapportDialog({ meetingId, meetingDate, onDone, onCancel }) {
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [assessment, setAssessment] = useState("");
  const [loading, setLoading] = useState(false);
  const [prevDate, setPrevDate] = useState(null);
  const md = (meetingDate || "").slice(0, 10);

  useEffect(() => {
    api.get("/le/meetings").then((r) => {
      const earlier = (r.data || [])
        .filter((m) => m.id !== meetingId && (m.date || "").slice(0, 10) < md)
        .map((m) => (m.date || "").slice(0, 10))
        .filter(Boolean)
        .sort();
      setPrevDate(earlier.length ? earlier[earlier.length - 1] : null);
    }).catch(() => {});
  }, [meetingId, md]);

  const fetchReport = async (f = from, t = to) => {
    setLoading(true);
    try {
      const r = await api.get("/le/sources/ledelsesrapport", { params: { date_from: f, date_to: t } });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  const useSinceLast = () => {
    if (!prevDate) return;
    setFrom(prevDate); setTo(md || ""); fetchReport(prevDate, md || "");
  };
  const done = () => {
    if (!data) { toast.error("Hent rapporten først"); return; }
    onDone({ id: uid(), type: "ledelsesrapport", report: data, assessment, fetched_at: data.fetched_at });
  };
  return (
    <div className="space-y-4" data-testid="le-input-ledelsesrapport">
      {prevDate && (
        <button type="button" data-testid="le-ledrap-since-last" onClick={useSinceLast}
          className="w-full flex items-center gap-2 rounded-xl border border-[#E9C9C3] bg-[#FBF6F5] px-3.5 py-2.5 text-left hover:bg-[#F7ECEA] transition-colors">
          <BarChart3 className="h-4 w-4 text-[#A83232] shrink-0" />
          <span className="text-sm text-[#1A1919] flex-1">Siden sidste LE-møde <span className="text-stone-500">({daDate(prevDate)} – {daDate(md)})</span></span>
          <span className="text-xs font-semibold text-[#A83232]">Brug periode</span>
        </button>
      )}
      <div className="grid grid-cols-2 gap-3 items-end">
        <div><p className="text-xs font-medium text-stone-500 mb-1.5">Fra</p><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-xl h-10" data-testid="le-ledrap-from" /></div>
        <div className="flex gap-2"><div className="flex-1"><p className="text-xs font-medium text-stone-500 mb-1.5">Til</p><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl h-10" data-testid="le-ledrap-to" /></div>
          <Button variant="outline" onClick={() => fetchReport()} className="rounded-xl border-[#EBE8E3] h-10" data-testid="le-ledrap-fetch">Hent</Button></div>
      </div>
      <p className="text-[11px] text-stone-400">Lad felterne stå tomme for at medtage hele perioden. Periode bygger på projekternes startdato.</p>
      {loading ? <div className="flex items-center gap-2 text-stone-400 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Henter…</div> : data && <LedelsesrapportView r={data} />}
      <div><p className="text-xs font-medium text-stone-500 mb-1.5">Ledelsens vurdering (valgfri)</p><Textarea data-testid="le-ledrap-assessment" value={assessment} onChange={(e) => setAssessment(e.target.value)} rows={2} className="rounded-xl" /></div>
      <DialogFooter><Button variant="ghost" onClick={onCancel} className="rounded-xl">Tilbage</Button><Button data-testid="le-ledrap-add" onClick={done} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">Tilføj</Button></DialogFooter>
    </div>
  );
}

// ---------------- Add-input entrypoint ----------------
export function AddInput({ meetingId, meetingDate, onAdd, trigger }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null);
  const close = () => { setOpen(false); setMode(null); };
  const done = (inp) => { onAdd(inp); close(); };
  const openDlg = () => { setMode(null); setOpen(true); };
  return (
    <>
      {trigger ? trigger(openDlg) : <Button variant="outline" size="sm" data-testid="le-add-input-btn" onClick={openDlg} className="rounded-lg border-[#EBE8E3] border-dashed"><Plus className="h-4 w-4 mr-1.5" /> Tilføj grundlag</Button>}
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="le-add-input-dialog">
          <DialogHeader><DialogTitle>{mode ? TYPES.find((t) => t.key === mode)?.label : "Tilføj grundlag"}</DialogTitle></DialogHeader>
          {!mode && <div className="space-y-3">
            <DropField onFile={async (f) => { try { done(await createMediaInput(meetingId, f, (f.type || "").startsWith("image/") ? "kpi" : "note")); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} testid="le-add-input-dropfield" />
            <div className="grid grid-cols-2 gap-3">
            {TYPES.map((t) => (
              <button key={t.key} data-testid={`le-input-type-${t.key}`} onClick={() => setMode(t.key)} className="rounded-xl border border-[#EBE8E3] p-4 text-left hover:border-[#A83232] hover:bg-[#FBF6F5] transition-colors">
                <t.icon className="h-5 w-5 text-[#A83232]" /><p className="text-sm font-medium text-[#1A1919] mt-2">{t.label}</p><p className="text-xs text-stone-500 mt-0.5">{t.desc}</p>
              </button>
            ))}
            </div>
          </div>}
          {mode === "audit" && <AuditDialog meetingDate={meetingDate} onDone={done} onCancel={() => setMode(null)} />}
          {mode === "reklamation" && <ReklamationDialog meetingDate={meetingDate} onDone={done} onCancel={() => setMode(null)} />}
          {mode === "reklanalyse" && <ReklAnalyseDialog onDone={done} onCancel={() => setMode(null)} />}
          {mode === "ledelsesrapport" && <LedelsesrapportDialog meetingId={meetingId} meetingDate={meetingDate} onDone={done} onCancel={() => setMode(null)} />}
          {(mode === "kpi" || mode === "note") && <MediaDialog type={mode} meetingId={meetingId} onDone={done} onCancel={() => setMode(null)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------- Input card (visning under dagsordenspunkt) ----------------
export function InputCard({ input, readonly, publicView, token, onRefresh, onRemove, onCreateAction, onLinkAction }) {
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(null);
  const imgSrc = (id) => publicView ? `${BACKEND}/api/public/le/${token}/images/${id}` : `${BACKEND}/api/le/images/${id}`;
  const T = INPUT_ICON[input.type] || StickyNote;
  const label = INPUT_LABEL[input.type];

  return (
    <div className="rounded-xl border border-[#EBE8E3] bg-white p-3.5" data-testid={`le-input-card-${input.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <T className="h-4 w-4 text-[#806B61]" /><span className="text-xs font-semibold text-[#806B61]">{label}</span>
        {input.fetched_at && <span className="text-[11px] text-stone-400 ml-auto">Hentet {daDate(input.fetched_at)}</span>}
      </div>

      {input.type === "audit" && <div className="space-y-2">
        {(input.audits || []).map((a) => (
          <div key={a.id} className="rounded-lg bg-[#FBFAF8] border border-[#F0EDE8] p-2.5">
            <div className="flex items-center gap-2 flex-wrap"><span className="font-mono text-xs text-stone-400">{a.audit_no}</span><span className="text-xs text-stone-500">{daDate(a.date)}</span></div>
            <p className="text-sm font-medium text-[#1A1919]">{a.title}</p>
            <p className="text-xs text-stone-500">Områder: {(a.areas || []).join(", ") || "—"}</p>
            <div className="flex flex-wrap gap-1.5 mt-1 text-[11px]">
              <span className="px-1.5 py-0.5 rounded bg-[#F1DEDA] text-[#A83232]">{a.counts?.Afvigelse || 0} afv.</span>
              <span className="px-1.5 py-0.5 rounded bg-[#F4EFEC] text-[#806B61]">{a.counts?.Observation || 0} obs.</span>
              <span className="px-1.5 py-0.5 rounded bg-[#E7EADF] text-[#5f6b4a]">{a.counts?.Forslag || 0} forslag</span>
              {(a.counts?.open || 0) > 0 && <span className="px-1.5 py-0.5 rounded bg-white border border-[#E9C9C3] text-[#A83232]">{a.counts.open} ikke-lukkede</span>}
            </div>
            {!publicView && <div className="flex gap-3 mt-1.5 text-xs">
              <a href={`/kvalitet/audit/${a.id}/rapport`} target="_blank" rel="noreferrer" className="text-[#806B61] hover:text-[#A83232] flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Se rapport</a>
              <a href={`/kvalitet/audit/fund`} target="_blank" rel="noreferrer" className="text-[#806B61] hover:text-[#A83232] flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Se fund</a>
            </div>}
          </div>
        ))}
      </div>}

      {input.type === "reklamation" && input.stats && <div className="rounded-lg bg-[#FBFAF8] border border-[#F0EDE8] p-2.5">
        <p className="text-xs text-stone-500">{input.stats.label} · <span className="italic">oprettelsesdato</span></p>
        <div className="flex flex-wrap gap-2 mt-1.5 text-sm">
          <span className="px-2 py-0.5 rounded bg-white border border-[#EBE8E3]">I alt: <b>{input.stats.total}</b></span>
          <span className="px-2 py-0.5 rounded bg-white border border-[#EBE8E3]">Åbne: <b className="text-[#A83232]">{input.stats.open}</b></span>
          <span className="px-2 py-0.5 rounded bg-white border border-[#EBE8E3]">Lukkede: <b className="text-[#5f6b4a]">{input.stats.closed}</b></span>
        </div>
        {input.stats.by_customer?.length > 0 && <p className="text-xs text-stone-600 mt-1.5"><span className="text-stone-400">Pr. kunde: </span>{input.stats.by_customer.slice(0, 6).map((c) => `${c.name} (${c.count})`).join(", ")}</p>}
        <p className="text-xs text-stone-600 mt-0.5"><span className="text-stone-400">Pr. årsag: </span>{input.stats.has_category_data ? input.stats.by_category.map((c) => `${c.name} (${c.count})`).join(", ") : <span className="italic text-stone-400">Ingen fejlkategori registreret</span>}</p>
        {(input.stats.selected_case_ids || []).length > 0 && <p className="text-[11px] text-stone-400 mt-1">{input.stats.selected_case_ids.length} sag(er) markeret til drøftelse — statistikken dækker alle {input.stats.total}.</p>}
        {(input.stats.cases || []).length > 0 && <button onClick={() => setOpen((o) => !o)} className="text-xs text-[#806B61] flex items-center gap-1 mt-1.5">{open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} {input.stats.cases.length} reklamationer</button>}
        {open && <div className="mt-1.5 max-h-44 overflow-y-auto space-y-0.5">{input.stats.cases.map((c) => (
          <div key={c.id} className={`flex items-center gap-2 text-xs px-1.5 py-1 rounded ${(input.stats.selected_case_ids || []).includes(c.id) ? "bg-[#F1DEDA]" : ""}`}>
            <span className="font-mono text-stone-400">{c.complaint_no || c.item_no || "—"}</span><span className="text-stone-600 truncate flex-1">{c.customer_name} · {c.title}</span><span className="text-stone-400">{daDate(c.created_at)}</span>
            {!publicView && <a href={`/kvalitetsrapport/${c.id}`} target="_blank" rel="noreferrer" className="text-[#806B61]"><ExternalLink className="h-3 w-3" /></a>}
          </div>
        ))}</div>}
      </div>}

      {input.type === "reklanalyse" && <div className="rounded-lg bg-[#FBF6F5] border border-[#F1DEDA] p-2.5">
        <div className="flex items-center gap-2 flex-wrap"><Sparkles className="h-3.5 w-3.5 text-[#806B61]" /><span className="text-sm font-medium text-[#1A1919]">{input.title}</span>
          {input.status && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F4EFEC] text-[#806B61]">{input.status}</span>}</div>
        {input.count != null && <p className="text-xs text-stone-500 mt-0.5">{input.count} reklamationer · {daDate(input.created_at)}</p>}
        {input.root_cause && <p className="text-sm text-stone-700 mt-1.5"><span className="text-xs text-[#A83232] font-medium">Rodårsag: </span>{input.root_cause}</p>}
        {input.summary && <p className="text-sm text-stone-600 mt-1">{input.summary}</p>}
        {(input.five_whys || []).length > 0 && <div className="mt-2">
          <p className="text-xs font-semibold text-stone-500">5 × Hvorfor</p>
          <ol className="mt-1 space-y-0.5 list-decimal list-inside">{input.five_whys.map((w, i) => <li key={i} className="text-xs text-stone-600">{w.why ? <><span className="text-stone-500">{w.why}</span> — {w.because}</> : (typeof w === "string" ? w : w.because)}</li>)}</ol>
        </div>}
        {(input.korrigerende || []).length > 0 && <div className="mt-2 rounded-lg bg-white/70 border border-[#F0E2DE] p-2">
          <p className="text-xs font-semibold text-[#A83232]">Korrigerende handlinger</p>
          <ul className="mt-1 space-y-0.5 list-disc list-inside">{input.korrigerende.map((r, i) => <li key={i} className="text-xs text-stone-700 whitespace-pre-wrap">{r}</li>)}</ul>
        </div>}
        {(input.forebyggende || []).length > 0 && <div className="mt-2 rounded-lg bg-white/70 border border-[#DCE3D0] p-2">
          <p className="text-xs font-semibold text-[#5f6b4a]">Forebyggende handlinger</p>
          <ul className="mt-1 space-y-0.5 list-disc list-inside">{input.forebyggende.map((r, i) => <li key={i} className="text-xs text-stone-700 whitespace-pre-wrap">{r}</li>)}</ul>
        </div>}
        {input.active_action && <p className="text-sm text-stone-700 mt-1.5"><span className="text-xs text-stone-400">Aktiv handling: </span>{input.active_action}</p>}
        {!publicView && input.analyse_id && <a href={`${input.rtype === "indkob_reklamation" ? "/indkoeb" : "/kvalitet"}/reklamationsanalyse/analyse/${input.analyse_id}`} target="_blank" rel="noreferrer" className="text-xs text-[#806B61] hover:text-[#A83232] flex items-center gap-1 mt-1.5"><ExternalLink className="h-3.5 w-3.5" /> Åbn reklamationsanalyse</a>}
      </div>}

      {input.type === "ledelsesrapport" && input.report && <LedelsesrapportView r={input.report} />}

      {(input.type === "kpi" || input.type === "note") && <div className="space-y-2">
        {(input.images || []).length > 0 && <div className="grid grid-cols-2 gap-2">{input.images.map((im) => (
          <figure key={im.image_id} className="rounded-lg border border-[#F0EDE8] p-1.5">
            <button type="button" onClick={() => setZoom(imgSrc(im.image_id))} data-testid={`le-image-zoom-${im.image_id}`} className="group relative block w-full cursor-zoom-in" title="Klik for at forstørre">
              <img src={imgSrc(im.image_id)} alt={im.caption || ""} className="w-full max-h-64 object-contain rounded bg-[#FBFAF8]" />
              <span className="absolute top-1.5 right-1.5 rounded-md bg-black/50 p-1 opacity-0 group-hover:opacity-100 transition-opacity"><ZoomIn className="h-3.5 w-3.5 text-white" /></span>
            </button>
            {im.caption && <figcaption className="text-[11px] text-stone-500 mt-1 text-center">{im.caption}</figcaption>}
          </figure>
        ))}</div>}
        {input.text && <p className="text-sm text-stone-700 whitespace-pre-wrap">{input.text}</p>}
        {(input.docs || []).length > 0 && <div className="space-y-1">{input.docs.map((d) => (
          <a key={d.image_id} href={imgSrc(d.image_id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#806B61] hover:text-[#A83232]"><FileText className="h-4 w-4" /> {d.filename}</a>
        ))}</div>}
      </div>}

      {input.assessment && <div className="mt-2 text-sm bg-[#FBF6F5] rounded-lg px-3 py-2 border border-[#F1DEDA]"><span className="text-xs text-[#A83232] font-medium">Ledelsens vurdering: </span><span className="text-stone-700">{input.assessment}</span></div>}

      {zoom && (
        <div onClick={() => setZoom(null)} className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 cursor-zoom-out" data-testid="le-image-lightbox">
          <img src={zoom} alt="" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <button onClick={() => setZoom(null)} className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 p-2 text-white" aria-label="Luk" data-testid="le-image-lightbox-close"><X className="h-5 w-5" /></button>
        </div>
      )}

      {!readonly && !publicView && <div className="flex flex-wrap gap-3 mt-2.5 pt-2 border-t border-[#F0EDE8]">
        {(input.type === "audit" || input.type === "reklamation") && <button onClick={() => onRefresh?.(input)} className="text-xs text-[#806B61] hover:underline flex items-center gap-1" data-testid={`le-input-refresh-${input.id}`}><RefreshCw className="h-3.5 w-3.5" /> Opdatér input</button>}
        <button onClick={() => onLinkAction?.(input)} className="text-xs text-[#806B61] hover:underline flex items-center gap-1" data-testid={`le-input-link-action-${input.id}`}><Link2 className="h-3.5 w-3.5" /> Knyt til handling</button>
        <button onClick={() => onRemove?.(input)} className="text-xs text-[#A83232] hover:underline flex items-center gap-1 ml-auto" data-testid={`le-input-remove-${input.id}`}><Trash2 className="h-3.5 w-3.5" /> Fjern</button>
      </div>}
    </div>
  );
}
